import { atom } from "jotai";

import { PatronEditBooking } from "../../../types";
import { currentBookingAtom } from "../../stores";
import { replacePlayerOnRoundAtom } from "../replacePlayerOnRoundAtom/replacePlayerOnRoundAtom";
import { normalizeGroupHosts } from "../setHostIdAndIndexOnRoundAtom/setHostIdAndIndexOnRoundAtom";

type Payload = {
  sourceRoundId: string;
  targetBookingIndex: number;
};

type SwapPayload = {
  sourceRoundId: string;
  targetRoundId: string;
};

type SwapRound = PatronEditBooking.Round;

const isGuestPlayer = (player: PatronEditBooking.RoundPlayer): boolean =>
  player.clientType === "guest" ||
  player.clientType === "newGuest" ||
  player.clientType === "unknownGuest";

const isMemberPlayer = (player: PatronEditBooking.RoundPlayer): boolean =>
  player.clientType === "member" || player.clientType === "customer";

export const canMovePlayerBetweenGroups = (
  rounds: SwapRound[],
  sourceRoundId: string,
  targetBookingIndex: number,
  groupCapacity?: number | null
): boolean => {
  const sourceRound = rounds.find((round) => round.localId === sourceRoundId);
  if (!sourceRound) return false;

  const sourceBookingIndex = sourceRound.bookingIndex ?? 0;
  if (sourceBookingIndex === targetBookingIndex) return false;
  if (
    rounds.filter((round) => (round.bookingIndex ?? 0) === sourceBookingIndex)
      .length <= 1
  )
    return false;
  if (
    !rounds.some(
      (round) =>
        round.localId !== sourceRoundId &&
        (round.bookingIndex ?? 0) === sourceBookingIndex &&
        round.player.id
    )
  )
    return false;

  const targetGroupSize = rounds.filter(
    (round) =>
      (round.bookingIndex ?? 0) === targetBookingIndex &&
      !round.isNonPlayingHost
  ).length;
  if (
    !sourceRound.isNonPlayingHost &&
    groupCapacity != null &&
    targetGroupSize >= groupCapacity
  )
    return false;

  const playersByGroup = new Map<number, PatronEditBooking.RoundPlayer[]>();
  rounds.forEach((round) => {
    const bookingIndex =
      round.localId === sourceRoundId
        ? targetBookingIndex
        : round.bookingIndex ?? 0;
    const players = playersByGroup.get(bookingIndex) ?? [];
    players.push(round.player);
    playersByGroup.set(bookingIndex, players);
  });

  return Array.from(playersByGroup.values()).every((players) => {
    const hasGuest = players.some(isGuestPlayer);
    return !hasGuest || players.some(isMemberPlayer);
  });
};

export const movePlayerBetweenGroupsAtom = atom(
  null,
  (get, set, { sourceRoundId, targetBookingIndex }: Payload) => {
    const booking = get(currentBookingAtom);
    if (!booking) return;

    const sourceRound = booking.rounds.find(
      (round) => round.localId === sourceRoundId
    );
    if (!sourceRound) return;
    if (
      !canMovePlayerBetweenGroups(
        booking.rounds,
        sourceRoundId,
        targetBookingIndex,
        booking.groupCapacity
      )
    ) {
      return;
    }

    const updatedRounds = booking.rounds.map((round) => {
      if (round.localId === sourceRoundId) {
        return { ...round, bookingIndex: targetBookingIndex };
      }

      return round;
    });

    set(currentBookingAtom, {
      ...booking,
      rounds: normalizeGroupHosts(updatedRounds),
    });
  }
);

export const canSwapPlayersBetweenGroups = (
  rounds: SwapRound[],
  sourceRoundId: string,
  targetRoundId: string,
  groupCapacity?: number | null
): boolean => {
  if (sourceRoundId === targetRoundId) return false;

  const sourceRound = rounds.find((round) => round.localId === sourceRoundId);
  const targetRound = rounds.find((round) => round.localId === targetRoundId);
  if (!sourceRound || !targetRound) return false;

  const sourceBookingIndex = sourceRound.bookingIndex ?? 0;
  const targetBookingIndex = targetRound.bookingIndex ?? 0;
  if (sourceBookingIndex === targetBookingIndex) return false;

  if (groupCapacity != null) {
    for (const groupIndex of [sourceBookingIndex, targetBookingIndex]) {
      const playingCount = rounds.filter((round) => {
        const swapped =
          round.localId === sourceRoundId
            ? targetRound
            : round.localId === targetRoundId
            ? sourceRound
            : round;
        return (
          (round.bookingIndex ?? 0) === groupIndex && !swapped.isNonPlayingHost
        );
      }).length;
      if (playingCount > groupCapacity) return false;
    }
  }

  const playersByGroup = new Map<number, PatronEditBooking.RoundPlayer[]>();
  rounds.forEach((round) => {
    const player =
      round.localId === sourceRoundId
        ? targetRound.player
        : round.localId === targetRoundId
        ? sourceRound.player
        : round.player;
    const players = playersByGroup.get(round.bookingIndex ?? 0) ?? [];
    players.push(player);
    playersByGroup.set(round.bookingIndex ?? 0, players);
  });

  return Array.from(playersByGroup.values()).every((players) => {
    const hasGuest = players.some(isGuestPlayer);
    return !hasGuest || players.some(isMemberPlayer);
  });
};

const swapRoundPlayerData = (
  round: PatronEditBooking.Round,
  replacementRound: PatronEditBooking.Round
): PatronEditBooking.Round => {
  // Persisted IDs belong to typed member/guest rounds, not to positions.
  return {
    ...replacementRound,
    bookingIndex: round.bookingIndex,
    index: round.index,
  };
};

export const swapPlayersBetweenGroupsAtom = atom(
  null,
  (get, set, { sourceRoundId, targetRoundId }: SwapPayload) => {
    const booking = get(currentBookingAtom);
    if (!booking || sourceRoundId === targetRoundId) return;

    const sourceRound = booking.rounds.find(
      (round) => round.localId === sourceRoundId
    );
    const targetRound = booking.rounds.find(
      (round) => round.localId === targetRoundId
    );
    if (!sourceRound || !targetRound) return;
    if (
      !canSwapPlayersBetweenGroups(
        booking.rounds,
        sourceRoundId,
        targetRoundId,
        booking.groupCapacity
      )
    ) {
      return;
    }

    const updatedRounds = booking.rounds.map((round) => {
      if (round.localId === sourceRoundId) {
        return swapRoundPlayerData(round, targetRound);
      }

      if (round.localId === targetRoundId) {
        return swapRoundPlayerData(round, sourceRound);
      }

      return round;
    });

    set(currentBookingAtom, {
      ...booking,
      rounds: normalizeGroupHosts(updatedRounds),
    });
  }
);

export const canReplacePlayerInGroup = (
  rounds: SwapRound[],
  sourceRoundId: string,
  player: PatronEditBooking.RoundPlayer
): boolean => {
  const sourceRound = rounds.find((round) => round.localId === sourceRoundId);
  if (!sourceRound) return false;
  if (player.id && rounds.some((round) => round.player.id === player.id)) {
    return false;
  }

  const groupPlayers = rounds
    .filter(
      (round) => (round.bookingIndex ?? 0) === (sourceRound.bookingIndex ?? 0)
    )
    .map((round) => (round.localId === sourceRoundId ? player : round.player));

  return !groupPlayers.some(isGuestPlayer) || groupPlayers.some(isMemberPlayer);
};

export const swapOrReplacePlayerOnRoundAtom = atom(
  null,
  (
    get,
    set,
    {
      roundId,
      player,
    }: { roundId: string; player: PatronEditBooking.RoundPlayer }
  ) => {
    const booking = get(currentBookingAtom);
    if (!booking) return false;

    const targetRound = player.id
      ? booking.rounds.find((round) => round.player.id === player.id)
      : undefined;

    if (targetRound) {
      if (
        !canSwapPlayersBetweenGroups(
          booking.rounds,
          roundId,
          targetRound.localId,
          booking.groupCapacity
        )
      ) {
        return false;
      }
      set(swapPlayersBetweenGroupsAtom, {
        sourceRoundId: roundId,
        targetRoundId: targetRound.localId,
      });
    } else {
      if (!canReplacePlayerInGroup(booking.rounds, roundId, player))
        return false;
      set(replacePlayerOnRoundAtom, { roundId, player });
    }

    return true;
  }
);
